import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Top-bar red dots that need an answer from this company:
 *  - incomingAccessRequests: buyers waiting for approval on our menus (My Menus)
 *  - newRequestMatches: listings that fit our buy requests, not yet opened (Buy Requests)
 * Both are RLS-scoped to the caller's company. Errors throw (never a silent zero).
 */
export const useNavAttention = () => {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['navAttention', user?.id],
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data: me, error: meErr } = await supabase.from('users').select('company_id').eq('id', user!.id).maybeSingle();
      if (meErr) throw meErr;
      const companyId = (me as { company_id?: string } | null)?.company_id;
      if (!companyId) return { incomingAccessRequests: 0, newRequestMatches: 0 };
      const [req, match] = await Promise.all([
        supabase.from('menu_access_requests' as never).select('id', { count: 'exact', head: true })
          .eq('supplier_company_id', companyId).eq('status', 'pending'),
        supabase.from('buyer_request_matches' as never).select('id', { count: 'exact', head: true })
          .eq('company_id', companyId).eq('status', 'new'),
      ]);
      if (req.error) throw req.error;
      if (match.error) throw match.error;
      return { incomingAccessRequests: req.count ?? 0, newRequestMatches: match.count ?? 0 };
    },
  });
  return data ?? { incomingAccessRequests: 0, newRequestMatches: 0 };
};
